/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 08-09-2026
 *
 * De taal van de interface: welke er zijn, welke er nu geldt, en hoe je hem wisselt. Bij het
 * opstarten wint de taal die de gebruiker eerder koos, daarna de browsertaal, en anders Engels.
 * Een keuze is te vergeten (`vergeetKeuze`), en dan geldt weer de automatisch bepaalde taal.
 *
 * De gekozen taal staat ook als signal, zodat een pipe die op de taal leunt vanzelf opnieuw
 * berekend wordt bij een wissel: een template-expressie is in Angular een reactieve lezer, dus een
 * signal dat daar gelezen wordt tekent het scherm opnieuw.
 */
import {inject, Injectable, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Settings} from 'luxon';
import {firstValueFrom} from 'rxjs';
import type {Taalcode} from '../types';
import {TAALALIASSEN, TALEN_TOKEN} from './taal.tokens';

/**
 * De taal waarop wordt teruggevallen: de enige die in alle vertaalbestanden compleet is.
 */
const TERUGVALTAAL: Taalcode = 'en';

/**
 * De sleutel waaronder de keuze van de gebruiker bewaard wordt.
 */
const OPSLAGSLEUTEL = 'dl-taal';

@Injectable({providedIn: 'root'})
export class TaalService {

    private readonly translateService = inject(TranslateService);

    /**
     * De talen die deze applicatie aanbiedt; zie `TALEN_TOKEN`.
     */
    private readonly talen = inject(TALEN_TOKEN);

    /**
     * De taalcodes die op een andere taal uitkomen; zie `TAALALIASSEN`.
     */
    private readonly aliassen = inject(TAALALIASSEN);

    /**
     * De taal die nu geldt.
     */
    private readonly _taal = signal<Taalcode>(TERUGVALTAAL);

    /**
     * Of de taal die nu geldt een bewaarde keuze van de gebruiker is.
     */
    private readonly _gekozen = signal(false);

    /**
     * De talen waarin de applicatie beschikbaar is.
     */
    readonly beschikbaar = this.talen;

    /**
     * De taal die nu geldt.
     */
    readonly taal = this._taal.asReadonly();

    /**
     * Of de taal die nu geldt door de gebruiker gekozen is (`true`, en dus bewaard) of automatisch
     * bepaald (`false`: de browsertaal of de terugvaltaal).
     */
    readonly gekozen = this._gekozen.asReadonly();

    /**
     * Zet de vertalingen klaar en kiest de begintaal. Wordt vanuit de app-initialisatie aangeroepen
     * en wacht op het vertaalbestand, zodat het eerste scherm meteen in de goede taal staat.
     *
     * Een automatisch bepaalde taal wordt niet bewaard: alleen een keuze uit `dl-taalkeuze` is een
     * keuze. Zou de browsertaal bij het eerste bezoek vastgelegd worden, dan zou een applicatie
     * zonder taalkeuze in de interface (create) een latere wisseling van browsertaal nooit meer
     * volgen.
     */
    async start(): Promise<void> {
        //de terugvaltaal zelf staat in `provideTranslateService`, zodat hij al geladen is
        this.translateService.addLangs([...this.talen]);
        const bewaard = this.bestaande(localStorage.getItem(OPSLAGSLEUTEL));
        await this.zet(bewaard ?? this.beginTaal(), !!bewaard);
    }

    /**
     * Wisselt van taal en wacht tot het vertaalbestand geladen is.
     * @param taal de gewenste taal
     * @param [bewaar] of deze taal de keuze van de gebruiker is, en dus onthouden moet worden
     */
    async zet(taal: Taalcode, bewaar = true): Promise<void> {
        await firstValueFrom(this.translateService.use(taal));
        Settings.defaultLocale = taal; //luxon toont datums en maandnamen in deze taal
        if (bewaar) {
            localStorage.setItem(OPSLAGSLEUTEL, taal);
        }
        this._taal.set(taal);
        this._gekozen.set(bewaar);
    }

    /**
     * Vergeet de bewaarde keuze en gaat terug naar de automatisch bepaalde taal.
     */
    async vergeetKeuze(): Promise<void> {
        localStorage.removeItem(OPSLAGSLEUTEL);
        await this.zet(this.beginTaal(), false);
    }

    /**
     * De taal waarmee begonnen wordt als de gebruiker niets gekozen heeft: de browsertaal, anders de
     * terugvaltaal.
     *
     * De browsertaal komt uit ngx-translate zelf, dat een regiovariant al terugbrengt tot zijn
     * basistaal (`nl-NL` wordt `nl`). Dat is dezelfde afspraak als in create, waar
     * `registerAvailableLanguageKeys` die omschrijving doet en `"*"` op `en` uitkomt — met dit
     * verschil dat create een taal kent die alleen als regiovariant bestaat (`pt-BR`); daarvoor is
     * `TAALALIASSEN`.
     */
    private beginTaal(): Taalcode {
        return this.bestaande(this.translateService.getBrowserLang()) ?? TERUGVALTAAL;
    }

    /**
     * De taal die bij deze code hoort, of `null` als de applicatie hem niet aanbiedt. De aliassen
     * gaan er vóór de toets over heen, zodat `pt` bij `pt-BR` uitkomt.
     */
    private bestaande(code: string | null | undefined): Taalcode | null {
        if (!code) {
            return null;
        }
        const taal = this.aliassen[code] ?? code;
        return this.talen.includes(taal) ? taal : null;
    }
}
